import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { styled, css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { DataLakeQuery } from "@graylog/enterprise-api";
import SearchFeatureExplanation from "data-lake/preview/SearchFeatureExplanation";
import DLLogViewWidget from "data-lake/preview/DLLogViewWidget";
import { Alert, Row } from "components/bootstrap";
import Search from "views/logic/search/Search";
import {
  execute,
  executeSearchJob,
  resetState as resetSearchExecutionState
} from "views/logic/slices/searchExecutionSlice";
import createSearch from "views/logic/slices/createSearch";
import useIsLoading from "views/hooks/useIsLoading";
import {
  PAGE_SIZE,
  INITIAL_PAGE,
  PREVIEW_JOB_STATUS,
  HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT,
  HIDE_DL_SEARCH_SUCCESSFUL_SEARCH_ALERT,
  DATA_LAKE_PREVIEW_QUERY_KEY,
  VALID_INITIAL_SEARCH_STATUSES
} from "data-lake/preview/Constants";
import useWidgetResults from "data-lake/preview/hooks/useWidgetResults";
import ProgressInformation from "data-lake/preview/ProgressInformation";
import Delayed from "components/common/Delayed";
import ExpiredSearchInfo from "data-lake/preview/ExpiredSearchInfo";
import { selectJobIds } from "views/logic/slices/searchExecutionSelectors";
import ExecutionInfo from "views/components/views/ExecutionInfo";
import useJobProgress from "data-lake/preview/hooks/useJobProgress";
import formatBackendMessages from "data-lake/preview/utils/formatBackendMessages";
import useDLPreviewDispatch from "data-lake/preview/store/useDLPreviewDispatch";
import useDLPreviewSelector from "data-lake/preview/store/useDLPreviewSelector";
import Store from "logic/local-storage/Store";
import stopSearchResultsPolling from "data-lake/preview/utils/stopSearchResultsPolling";
import { cancelJob } from "views/logic/slices/executeJobResult";
import SuccessfulSearchInfo from "data-lake/preview/SuccessfulSearchInfo";
import UserNotification from "util/UserNotification";
import IfFeatureEnabled from "components/features/IfFeatureEnabled";
import DataLakeTypeSelect from "data-lake/preview/DataLakeTypeSelect";
import useWidgetConfiguration from "data-lake/preview/hooks/useWidgetConfiguration";
const WidgetContainer = styled.div`
  flex: 1;
  height: 100%;
  overflow: auto;
`;
const FullHeightRow = styled(Row)`
  flex: 1;
  overflow: auto;
  display: flex;
`;
const StyledRow = styled(Row)(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.xs};
  `
);
const StyledAlert = styled(Alert)`
  margin: 0;
`;
const Header = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    margin-bottom: ${theme.spacings.xs};
    gap: ${theme.spacings.sm};
    justify-content: space-between;
  `
);
const LeftHeaderCol = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.lg};
  `
);
const RightHeaderCol = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.sm};
  `
);
const Headline = styled.h1(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.body};
  `
);
const reexecuteSearchJob = (searchJobId, executingNodeId) => (dispatch, _getState, { searchExecutors }) => dispatch(
  executeSearchJob({
    stopPolling: (progress) => stopSearchResultsPolling(INITIAL_PAGE, progress),
    searchExecutors,
    jobIds: {
      asyncSearchId: searchJobId,
      nodeId: executingNodeId
    },
    page: INITIAL_PAGE,
    perPage: PAGE_SIZE
  })
).catch(
  (error) => UserNotification.error(`Fetching search results failed with status: ${error.message}`)
);
const usePollResultsForInitialSearchJob = (searchJobStatus, searchJobProgress, searchJobId, executingNodeId) => {
  const dispatch = useDLPreviewDispatch();
  useEffect(() => {
    if (searchJobStatus === PREVIEW_JOB_STATUS.running && !stopSearchResultsPolling(INITIAL_PAGE, searchJobProgress)) {
      dispatch(reexecuteSearchJob(searchJobId, executingNodeId));
    }
  }, [dispatch, executingNodeId, searchJobId, searchJobProgress, searchJobStatus]);
};
const useMostRecentJobIds = (initialSearchJob, initialSearchId, executedSearchId) => {
  const jobIds = useDLPreviewSelector(selectJobIds);
  const initialSearchJobId = initialSearchJob.status === PREVIEW_JOB_STATUS.running || initialSearchJob.status === PREVIEW_JOB_STATUS.done ? {
    asyncSearchId: initialSearchJob.id,
    nodeId: initialSearchJob.nodeId,
    searchId: initialSearchId
  } : void 0;
  const [mostRecentJobIds, setMostRecentJobIds] = useState(initialSearchJobId);
  useEffect(() => {
    if (jobIds && ((mostRecentJobIds == null ? void 0 : mostRecentJobIds.asyncSearchId) !== jobIds.asyncSearchId || (mostRecentJobIds == null ? void 0 : mostRecentJobIds.nodeId) !== jobIds.nodeId)) {
      setMostRecentJobIds({ ...jobIds, searchId: executedSearchId });
    }
  }, [executedSearchId, jobIds, mostRecentJobIds]);
  return mostRecentJobIds;
};
const resetAlertFlags = () => {
  Store.set(HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT, "false");
  Store.set(HIDE_DL_SEARCH_SUCCESSFUL_SEARCH_ALERT, "false");
};
const DataLakePreview = ({
  filtersFromSearchType,
  initialSearch,
  initialSearchJob,
  queryFromFormValues,
  resetUrlQueryParams,
  searchBar: SearchBar,
  widgetFromFormValues,
  headerActions = void 0,
  leftCol = void 0
}) => {
  var _a;
  const dispatch = useDLPreviewDispatch();
  const searchBarFormRef = useRef(null);
  const [executedSearch, setExecutedSearch] = useState(
    VALID_INITIAL_SEARCH_STATUSES.includes(initialSearchJob.status) ? initialSearch : void 0
  );
  const { widgetData, errors, executedAt, duration } = useWidgetResults(executedSearch);
  const mostRecentJobIds = useMostRecentJobIds(initialSearchJob, initialSearch == null ? void 0 : initialSearch.id, executedSearch == null ? void 0 : executedSearch.id);
  const queryClient = useQueryClient();
  const invalidateDataLakeQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["data-lake"]
    });
    queryClient.invalidateQueries({
      queryKey: [DATA_LAKE_PREVIEW_QUERY_KEY]
    });
  }, [queryClient]);
  const { widget, resetWidget, setWidget } = useWidgetConfiguration();
  const [editing, setEditing] = useState(false);
  const { data: jobProgress, setEnableProgressFetching } = useJobProgress(
    mostRecentJobIds,
    initialSearchJob.status,
    executedSearch == null ? void 0 : executedSearch.id
  );
  const isExecutionFirstJob = useIsLoading();
  const searchJobIsRunning = isExecutionFirstJob || (jobProgress == null ? void 0 : jobProgress.status) === PREVIEW_JOB_STATUS.running;
  usePollResultsForInitialSearchJob(
    initialSearchJob.status,
    initialSearchJob.progress,
    initialSearchJob.id,
    initialSearchJob.nodeId
  );
  const onChangeWidget = useCallback(
    (newWidget) => {
      setWidget(newWidget);
      return Promise.resolve();
    },
    [setWidget]
  );
  const toggleEdit = useCallback(() => setEditing((cur) => !cur), []);
  const onReset = useCallback(() => {
    setExecutedSearch(void 0);
    resetWidget();
    setEditing(false);
    resetUrlQueryParams();
    return DataLakeQuery.resetActiveQuery().then(() => {
      invalidateDataLakeQueries();
      return dispatch(resetSearchExecutionState());
    });
  }, [dispatch, invalidateDataLakeQueries, resetUrlQueryParams, resetWidget]);
  const onSearchBarSubmit = useCallback(
    async (formValues) => {
      resetUrlQueryParams();
      resetAlertFlags();
      const updatedWidget = widgetFromFormValues(widget, formValues);
      const query = queryFromFormValues(updatedWidget, formValues);
      const search = await createSearch(Search.create().toBuilder().queries([query]).build()).catch(
        (error) => UserNotification.error(`Creating a search failed with status: ${error.message}`)
      );
      if (!search) {
        return Promise.resolve();
      }
      dispatch(resetSearchExecutionState());
      setWidget(updatedWidget);
      setEditing(false);
      setExecutedSearch(search);
      setEnableProgressFetching(true);
      return dispatch(
        execute({
          search,
          activeQuery: search.queries.first().id,
          page: INITIAL_PAGE,
          perPage: PAGE_SIZE,
          stopPolling: (progress) => stopSearchResultsPolling(INITIAL_PAGE, progress)
        })
      ).catch(
        (error) => UserNotification.error(`Executing new search failed with status: ${error.message}`)
      ).then(() => {
        invalidateDataLakeQueries();
      });
    },
    [
      dispatch,
      invalidateDataLakeQueries,
      queryFromFormValues,
      resetUrlQueryParams,
      setEnableProgressFetching,
      setWidget,
      widget,
      widgetFromFormValues
    ]
  );
  const messages = useMemo(() => formatBackendMessages(widgetData == null ? void 0 : widgetData.messages), [widgetData == null ? void 0 : widgetData.messages]);
  const hasResult = (messages == null ? void 0 : messages.length) > 0;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SearchFeatureExplanation, null), initialSearchJob.status === PREVIEW_JOB_STATUS.expired && initialSearch && !executedSearch && !searchJobIsRunning && /* @__PURE__ */ React.createElement(
    ExpiredSearchInfo,
    {
      filtersFromSearchType,
      expiredSearch: initialSearch,
      searchBarFormRef
    }
  ), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement(LeftHeaderCol, null, /* @__PURE__ */ React.createElement(Headline, null, "Data Lake Preview"), /* @__PURE__ */ React.createElement(IfFeatureEnabled, { name: "external_data_lake_search" }, /* @__PURE__ */ React.createElement(DataLakeTypeSelect, null))), /* @__PURE__ */ React.createElement(RightHeaderCol, null, /* @__PURE__ */ React.createElement(
    ExecutionInfo,
    {
      duration,
      executedAt,
      executionFinished: executedSearch && !searchJobIsRunning,
      showTotal: false
    }
  ), headerActions))), /* @__PURE__ */ React.createElement(
    SearchBar,
    {
      executedSearch,
      formRef: searchBarFormRef,
      onReset,
      onSubmit: onSearchBarSubmit,
      searchJobIsRunning
    }
  ), executedSearch && /* @__PURE__ */ React.createElement(React.Fragment, null, (jobProgress == null ? void 0 : jobProgress.status) === PREVIEW_JOB_STATUS.running && /* @__PURE__ */ React.createElement(Delayed, { delay: initialSearchJob.status === PREVIEW_JOB_STATUS.running ? 0 : 300 }, /* @__PURE__ */ React.createElement(StyledRow, null, /* @__PURE__ */ React.createElement(
    ProgressInformation,
    {
      onCancel: () => {
        setExecutedSearch(void 0);
        if (mostRecentJobIds) {
          return cancelJob(mostRecentJobIds);
        }
        return Promise.resolve();
      },
      progress: jobProgress.progress
    }
  ))), ((initialSearch == null ? void 0 : initialSearch.id) === executedSearch.id && (initialSearchJob == null ? void 0 : initialSearchJob.status) === PREVIEW_JOB_STATUS.timeout || (jobProgress == null ? void 0 : jobProgress.status) === PREVIEW_JOB_STATUS.timeout) && /* @__PURE__ */ React.createElement(StyledRow, null, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, "The timeout for this preview search has been reached. Not all messages have been retrieved. Please adjust your query parameters in case the current results don\u2019t show what you are looking for.")), errors.length > 0 && /* @__PURE__ */ React.createElement(StyledRow, null, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, errors.map((error) => error.description))), errors.length === 0 && /* @__PURE__ */ React.createElement(React.Fragment, null, (jobProgress == null ? void 0 : jobProgress.status) === PREVIEW_JOB_STATUS.done && /* @__PURE__ */ React.createElement(SuccessfulSearchInfo, { executedAt }))), !!widget && /* @__PURE__ */ React.createElement(FullHeightRow, null, leftCol ? leftCol(searchBarFormRef) : null, /* @__PURE__ */ React.createElement(WidgetContainer, null, /* @__PURE__ */ React.createElement(
    DLLogViewWidget,
    {
      hasExecutedSearch: !!executedSearch,
      initialPageTotal: (_a = widgetData == null ? void 0 : widgetData.total) != null ? _a : 0,
      mostRecentJobIds,
      effectiveTimerange: widgetData == null ? void 0 : widgetData.effectiveTimerange,
      messages,
      isFetchingFirstPage: searchJobIsRunning && !hasResult,
      onToggleEdit: toggleEdit,
      onChangeWidget,
      widget,
      editing
    }
  ))));
};
export default DataLakePreview;
